import { getCookie, getSearchParam, normalizeUrl } from '@/util/url'
import { IGif } from '@giphy/js-types'
import getAdTestResponse from './ad-mocks/ad-test-response'
import { processDecision } from './ads-util'
import { giphyLocalUrl, publicRuntimeConfig } from './env'

export type AdEndpointViews =
    // spotlights that appear in a grid
    | 'xl_carousel_banner'
    | 'home_video_spotlight'
    | 'search_results_video_spotlight'
    | 'gif_details_video_spotlight'
    | 'homepage_banner' // bouncer banner
    // geo views
    | 'home' // geo home page banner
    | 'gif_details' // gif detail banner
    | 'search_results' // search page banner
    | 'home_grid_0' // geo home grid
    | 'gif_details_grid_0' // geo gif details grid
    | 'search_results_grid_0' // geo search grid

export const gridPathnameMap: Record<string, AdEndpointViews> = {
    '/gifs': 'gif_details_grid_0',
    '/search': 'search_results_grid_0',
    '/': 'home_grid_0',
}

export type AdDecisionAnalytics = {
    start?: string
    firstq?: string
    midpoint?: string
    thirdq?: string
    complete?: string
    mute?: string
    unmute?: string
    pause?: string
    rewind?: string
    resume?: string
    // Allow unexpected/extra event names without losing type safety for known ones
    [eventName: string]: string | undefined
}

export type AdDecision = {
    gif: IGif
    subdecisions?: AdDecision[]
    cta_text?: string
    cta_url?: string
    click_url?: string
    impression_url?: string
    cta_tracking_url?: string
    click_tracking_url?: string
    analytics?: AdDecisionAnalytics
    is_clickable?: boolean
}

export type AdResponse = {
    decisions: Record<string, AdDecision | null>
}

export const hasAnyAd = (adResponse?: AdResponse) => {
    const decisions = adResponse?.decisions || {}
    return Object.keys(decisions).some((key) => {
        return decisions[key] !== null
    })
}

export const getAdDecisionByType = (response: AdResponse | undefined, id?: AdEndpointViews) =>
    id ? (response?.decisions?.[id] as AdDecision) : undefined

export const getClickableAd = (response: AdResponse | undefined, id: AdEndpointViews) => {
    const decision = getAdDecisionByType(response, id)
    if (decision?.is_clickable) {
        return decision
    } else {
        return undefined
    }
}

export const hasAd = (type: AdEndpointViews, adResponse?: AdResponse) => {
    return !!getAdDecisionByType(adResponse, type)
}

export const mergeResponses = (response1?: AdResponse | undefined, response2?: AdResponse): AdResponse => {
    return {
        decisions: { ...(response1?.decisions || {}), ...(response2?.decisions || {}) },
    }
}

export type AdEndpointRequestOptions = {
    views: AdEndpointViews[]
    rating?: string
    keywords?: string[]
    cookie?: string
    accessToken?: string
    platform?: string
    headers?: HeadersInit
    devMode?: boolean
    searchParams?: URLSearchParams
}

export type AdEndpointRequestBody = {
    keywords: string[]
    rating: string
    views: AdEndpointViews[]
    platform: string
    testing: boolean
    use_mock: boolean
}

const headerEntriesFromInit = (headersInit?: HeadersInit): [string, string][] => {
    if (!headersInit) return []
    if (headersInit instanceof Headers) return Array.from(headersInit.entries())
    if (Array.isArray(headersInit)) return headersInit
    return Object.entries(headersInit)
}

/**
 * Fetch from the ads endpoint, this needs to be invoked from both the server and the client
 */
export const fetchAdFromEndpoint = async ({
    views,
    rating = 'unrated',
    keywords = [],
    accessToken = '',
    cookie = '',
    devMode,
    platform = 'web',
    headers: incomingHeaders = {},
    searchParams,
}: AdEndpointRequestOptions): Promise<[AdResponse?, string?]> => {
    const body: AdEndpointRequestBody = {
        keywords,
        rating,
        views,
        platform,
        testing: (getCookie('ads_dev_network', cookie) === 'true' || devMode) ?? false,
        use_mock: getCookie('ads_use_mock', cookie) === 'true',
    }
    // load test1.json here if there is a searchParam that equals ad_test=1
    const adTestValue = getAdTestValue(searchParams)
    if (!isNaN(adTestValue)) {
        const [mockResponse, mockError] = await getAdTestResponse(adTestValue)
        if (!mockResponse) {
            return [, mockError]
        }
        const processedDecisions: Record<string, AdDecision | null> = Object.fromEntries(
            Object.entries(mockResponse?.decisions || {}).map(([key, decision]) => {
                const newDecision = decision ? processDecision(decision) : null
                if (newDecision) {
                    return [key, normalizeDecisionUrls(newDecision as AdDecision)] as [string, AdDecision]
                }
                return [key, newDecision] as [string, null]
            })
        )
        return [{ decisions: processedDecisions }, mockError]
    }
    if (publicRuntimeConfig.isPlaywright) {
        return [, 'Running Playwright test']
    }
    const fetchUrl = `${giphyLocalUrl}/api/v2/ads/`
    try {
        const forwardedHeaderEntries = headerEntriesFromInit(incomingHeaders).filter(
            ([key]) => key.toLowerCase() !== 'content-length'
        )
        let headers: HeadersInit = {
            ...Object.fromEntries(forwardedHeaderEntries),
            'content-type': 'application/json',
            ...(accessToken ? { authorization: `Token ${accessToken}` } : {}),
            cookie,
            'X-CSRFToken': getCookie('csrftoken', cookie),
        }
        // console.log(`ad endpoint request ${fetchUrl}`, JSON.stringify(body), headers)
        const response = await fetch(`${fetchUrl}${searchParams ? `?${searchParams.toString()}` : ''}`, {
            method: 'POST',
            body: JSON.stringify(body),
            headers,
        })
        if (response.ok) {
            try {
                const result = (await response.json()) as AdResponse
                // console.log('ads-endpoint response', result)
                // Process decisions: append `ap=1` to every URL under gif.images for ad attribution
                let processedDecisions: Record<string, AdDecision | null> = {}
                processedDecisions = Object.fromEntries(
                    Object.entries(result?.decisions || {}).map(([key, decision]) => {
                        const newDecision = decision ? processDecision(decision) : null
                        if (newDecision) {
                            return [key, normalizeDecisionUrls(newDecision as AdDecision)] as [string, AdDecision]
                        }
                        return [key, newDecision] as [string, null]
                    })
                )

                return [{ decisions: processedDecisions }]
            } catch (jsonError) {
                console.error('Failed to parse ad endpoint response as JSON', jsonError)
                return [, `Status:${response.status} ${jsonError}`]
            }
        }
        let text = ''
        try {
            text = await response.text()
        } catch (_e) {}
        return [, `Status:${response.status} ${text}`]
    } catch (e) {
        console.error(e)
        return [, e as any]
    }
}

export function getAdTestValue(searchParams: URLSearchParams | undefined) {
    return parseInt(getSearchParam('ad_test', searchParams) || '')
}

const normalizeDecisionUrls = (decision: AdDecision): AdDecision => ({
    ...decision,
    cta_url: normalizeUrl(decision.cta_url),
    // a legacy response will use the click_url for cta tracking
    cta_tracking_url: normalizeUrl(decision.cta_tracking_url || decision.click_url),
    // if click_tracking_url is missing (legacy response), omit click_url so UI falls back to gif.url
    click_url: normalizeUrl(decision.click_tracking_url ? decision.click_url : undefined),
    // a legacy response will use the click_url for media click tracking
    click_tracking_url: normalizeUrl(decision.click_tracking_url || decision.click_url),
    subdecisions: decision.subdecisions?.map((sub) => normalizeDecisionUrls(sub)),
})
